import { observer } from "mobx-react";
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  IconButton,
  useTheme,
  Grid2,
  Chip,
} from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { Vendor } from "../../../models/Vendors/Vendor";
import { formatDate } from "../../components/common/functions/formatDate";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { HelperAPI } from "../../../api/HelperAPI";
import { ensureUrlProtocol } from "../../../utils/urlHelper";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import ConfirmationPopup from "../../components/common/pop-up/ConfirmationPopup";

interface VendorDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  vendor: Vendor | null;
}

const VendorDetailsPopup: React.FC<VendorDetailsPopupProps> = ({
  open,
  onClose,
  vendor,
}) => {
  const theme = useTheme();
  const { vendorStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [rejectType, setRejectType] = useState<"finance" | "sourcing" | null>(
    null,
  );
  const [approveType, setApproveType] = useState<"finance" | "sourcing" | null>(
    null,
  );

  const handleDownloadLink = (url: string, defaultFilename: string | null) => {
    if (!url || url.trim() === "") return;
    HelperAPI.downloadVendorFiles(url, defaultFilename).catch(
      (err: unknown) => {
        showError(
          "Download failed",
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || "Failed to download file",
        );
      },
    );
  };

  const isPrimaryPoc = (poc: any) => {
    return poc.is_primary === "1" || String(poc.is_primary) === "1";
  };

  const getSortedPocList = () => {
    if (!vendor?.poc_list) return [];
    return [...vendor.poc_list].sort((a, b) => {
      const aIsPrimary = isPrimaryPoc(a);
      const bIsPrimary = isPrimaryPoc(b);
      if (aIsPrimary && !bIsPrimary) return -1;
      if (!aIsPrimary && bIsPrimary) return 1;
      return 0;
    });
  };

  const handleApprove = async () => {
    if (!vendor?.id || !approveType) return;

    const hasFinancePermission = userStore.hasPermission([
      EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_FINANCE,
    ]);
    const hasSourcingPermission = userStore.hasPermission([
      EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_SOURCING,
    ]);

    if (approveType === "finance" && !hasFinancePermission) {
      showError(
        "Error",
        "You are not authorized to approve finance vendor requests",
      );
      return;
    }

    if (approveType === "sourcing" && !hasSourcingPermission) {
      showError(
        "Error",
        "You are not authorized to approve sourcing vendor requests",
      );
      return;
    }

    setIsLoading(true);
    try {
      if (approveType === "finance") {
        await vendorStore.approveFinanceVendorRequest(
          vendor.id,
          userStore.user?.id || undefined,
          userStore.user?.name || undefined,
        );
        showSuccess("Finance approval successful");
      } else {
        await vendorStore.approveSourcingVendorRequest(
          vendor.id,
          userStore.user?.id || undefined,
          userStore.user?.name || undefined,
        );
        showSuccess("Sourcing approval successful");
      }
      setShowApproveDialog(false);
      setApproveType(null);
      onClose();
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Something went wrong! Please try again later.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const openApproveDialog = (type: "finance" | "sourcing") => {
    setApproveType(type);
    setShowApproveDialog(true);
  };

  const handleReject = async (reason: string) => {
    if (!vendor?.id || !rejectType) return;

    const hasFinancePermission = userStore.hasPermission([
      EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_FINANCE,
    ]);
    const hasSourcingPermission = userStore.hasPermission([
      EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_SOURCING,
    ]);

    if (rejectType === "finance" && !hasFinancePermission) {
      showError(
        "Error",
        "You are not authorized to reject finance vendor requests",
      );
      return;
    }

    if (rejectType === "sourcing" && !hasSourcingPermission) {
      showError(
        "Error",
        "You are not authorized to reject sourcing vendor requests",
      );
      return;
    }

    setIsLoading(true);
    try {
      if (rejectType === "finance") {
        await vendorStore.rejectFinanceVendorRequest(
          vendor.id,
          reason,
          userStore.user?.id || undefined,
          userStore.user?.name || undefined,
        );
        showSuccess("Request rejected");
      } else {
        await vendorStore.rejectSourcingVendorRequest(
          vendor.id,
          reason,
          userStore.user?.id || undefined,
          userStore.user?.name || undefined,
        );
        showSuccess("Request rejected");
      }
      setShowRejectDialog(false);
      setRejectType(null);
      onClose();
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Something went wrong! Please try again later.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const openRejectDialog = (type: "finance" | "sourcing") => {
    setRejectType(type);
    setShowRejectDialog(true);
  };

  const canApproveFinance =
    vendor &&
    (!vendor.finance_approval || vendor.finance_approval === 0) &&
    (!vendor.finance_rejection || vendor.finance_rejection === 0);
  const canApproveSourcing =
    vendor &&
    (!vendor.sourcing_approval || vendor.sourcing_approval === 0) &&
    (!vendor.sourcing_rejection || vendor.sourcing_rejection === 0);
  const canRejectFinance =
    vendor &&
    (!vendor.finance_approval || vendor.finance_approval === 0) &&
    (!vendor.finance_rejection || vendor.finance_rejection === 0);
  const canRejectSourcing =
    vendor &&
    (!vendor.sourcing_approval || vendor.sourcing_approval === 0) &&
    (!vendor.sourcing_rejection || vendor.sourcing_rejection === 0);

  const hasFinancePermission = userStore.hasPermission([
    EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_FINANCE,
  ]);
  const hasSourcingPermission = userStore.hasPermission([
    EmployeeRoles.APPROVE_REJECT_VENDOR_REQUESTS_SOURCING,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            minHeight: "50vh",
            maxHeight: { xs: "100vh", sm: "90vh" },
            height: { xs: "100vh", sm: "auto" },
            maxWidth: {
              xs: "90vw",
              sm: "85vw",
              md: "80vw",
              lg: "70vw",
              xl: "60vw",
            },
            width: {
              xs: "90vw",
              sm: "85vw",
              md: "80vw",
              lg: "70vw",
              xl: "60vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          height: { xs: "100%", sm: "auto" },
          maxHeight: { xs: "100vh", sm: "90vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        {!vendor ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 4,
              minHeight: "200px",
            }}
          >
            <CustomTypography
              variant="body1"
              sx={{ color: theme.palette.text.secondary }}
            >
              No vendor data available
            </CustomTypography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
                borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <VisibilityIcon
                  sx={{
                    color: theme.palette.customColors.blueGradientStart,

                    marginTop: 0.2,
                  }}
                />
                <CustomTypography variant="heading4">
                  Vendor Details{" "}
                </CustomTypography>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  color: theme.palette.customColors.greyTextColor,
                  "&:hover": {
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                    color: theme.palette.customColors.blackBorder,
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <DialogContent
              sx={{
                padding: { xs: "20px", sm: "24px" },
                flex: 1,
                overflow: "auto",
                maxHeight: { xs: "calc(100vh - 120px)", sm: "70vh" },
                display: "flex",
                flexDirection: "column",
                gap: 3,
                "&::-webkit-scrollbar": { width: "8px" },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: theme.palette.customColors.greyBackground,
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.customColors.greyDarkborder,
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: theme.palette.customColors.greyTextColor,
                  },
                },
              }}
            >
              {/* Approval/Rejection Status - Professional UI at Top */}
              <Box
                sx={{
                  mb: 3,
                  p: 3,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                }}
              >
                <CustomTypography
                  variant="heading5"
                  style={{
                    fontWeight: 700,
                    marginBottom: 20,
                    color: theme.palette.customColors.blackBorder,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 24,
                      borderRadius: 1,
                      background: `linear-gradient(180deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 100%)`,
                    }}
                  />
                  Approval Status
                </CustomTypography>
                <Grid2 container spacing={3}>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        p: 3,
                        border: `1.5px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                        height: "100%",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CustomTypography
                        variant="body1"
                        style={{
                          fontWeight: 700,
                          marginBottom: 8,
                        }}
                      >
                        Finance Approval
                      </CustomTypography>
                      {vendor.finance_approval === 1 ? (
                        <Box>
                          <Chip
                            label="Approved"
                            color="success"
                            size="medium"
                            sx={{
                              mb: 2.5,
                              fontWeight: 700,
                              px: 1,
                            }}
                          />
                          <Grid2 container spacing={2}>
                            {vendor.finance_approval_date && (
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.75rem",
                                    marginBottom: "0.5rem",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Approved Date
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.success.main,
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {formatDate(vendor.finance_approval_date)}
                                </CustomTypography>
                              </Grid2>
                            )}
                            {(vendor.finance_approved_by_admin_name ||
                              (vendor as any)
                                .finance_approval_by_admin_name) && (
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.75rem",
                                    marginBottom: "0.5rem",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Approved By
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    color: theme.palette.text.primary,
                                  }}
                                >
                                  {vendor.finance_approved_by_admin_name ||
                                    (vendor as any)
                                      .finance_approval_by_admin_name}
                                </CustomTypography>
                              </Grid2>
                            )}
                          </Grid2>
                        </Box>
                      ) : vendor.finance_rejection === 1 ? (
                        <Box>
                          <Chip
                            label="Rejected"
                            color="error"
                            size="medium"
                            sx={{
                              mb: 2.5,
                              fontWeight: 700,
                              padding: "0.5rem",
                            }}
                          />

                          <Grid2 container spacing={2}>
                            {vendor.finance_rejection_date && (
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.75rem",
                                    marginBottom: "0.5rem",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Rejected Date
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    color: theme.palette.error.main,
                                  }}
                                >
                                  {formatDate(vendor.finance_rejection_date)}
                                </CustomTypography>
                              </Grid2>
                            )}
                            {(vendor.finance_rejected_by_admin_name ||
                              (vendor as any)
                                .finance_rejection_by_admin_name) && (
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.75rem",
                                    marginBottom: "0.5rem",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Rejected By
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    color: theme.palette.text.primary,
                                  }}
                                >
                                  {vendor.finance_rejected_by_admin_name ||
                                    (vendor as any)
                                      .finance_rejection_by_admin_name}
                                </CustomTypography>
                              </Grid2>
                            )}
                          </Grid2>
                          {vendor.finance_rejection_reason && (
                            <Box sx={{ mb: 0.5, mt: 2 }}>
                              <CustomTypography
                                variant="body2"
                                style={{
                                  color: theme.palette.error.main,
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  marginBottom: "0.5rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Rejection Reason
                              </CustomTypography>
                              <CustomTypography variant="body2">
                                {vendor.finance_rejection_reason}
                              </CustomTypography>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          <Chip
                            label="Pending"
                            color="warning"
                            size="medium"
                            sx={{
                              fontWeight: 700,
                              px: 1,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        p: 3,
                        border: `1.5px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                        height: "100%",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CustomTypography
                        variant="body1"
                        style={{
                          fontWeight: 700,
                          marginBottom: 8,
                        }}
                      >
                        Sourcing Approval
                      </CustomTypography>
                      {vendor.sourcing_approval === 1 ? (
                        <Box>
                          <Chip
                            label="Approved"
                            color="success"
                            size="medium"
                            sx={{
                              mb: 2.5,
                              fontWeight: 700,
                              px: 1,
                            }}
                          />
                          <Grid2 container spacing={2}>
                            {vendor.sourcing_approval_date && (
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.75rem",
                                    marginBottom: "0.5rem",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Approved Date
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.success.main,
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {formatDate(vendor.sourcing_approval_date)}
                                </CustomTypography>
                              </Grid2>
                            )}
                            {(vendor.sourcing_approved_by_admin_name ||
                              (vendor as any)
                                .sourcing_approval_by_admin_name) && (
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.75rem",
                                    marginBottom: "0.5rem",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Approved By
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    color: theme.palette.text.primary,
                                  }}
                                >
                                  {vendor.sourcing_approved_by_admin_name ||
                                    (vendor as any)
                                      .sourcing_approval_by_admin_name}
                                </CustomTypography>
                              </Grid2>
                            )}
                          </Grid2>
                        </Box>
                      ) : vendor.sourcing_rejection === 1 ? (
                        <Box>
                          <Chip
                            label="Rejected"
                            color="error"
                            size="medium"
                            style={{
                              marginBottom: "1rem",
                              fontWeight: 700,
                              padding: "0.5rem",
                            }}
                          />

                          <Grid2 container spacing={2}>
                            {vendor.sourcing_rejection_date && (
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.75rem",
                                    marginBottom: "0.5rem",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Rejected Date
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    color: theme.palette.error.main,
                                  }}
                                >
                                  {formatDate(vendor.sourcing_rejection_date)}
                                </CustomTypography>
                              </Grid2>
                            )}
                            {(vendor.sourcing_rejected_by_admin_name ||
                              (vendor as any)
                                .sourcing_rejection_by_admin_name) && (
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.75rem",
                                    marginBottom: "0.5rem",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Rejected By
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    color: theme.palette.text.primary,
                                  }}
                                >
                                  {vendor.sourcing_rejected_by_admin_name ||
                                    (vendor as any)
                                      .sourcing_rejection_by_admin_name}
                                </CustomTypography>
                              </Grid2>
                            )}
                          </Grid2>
                          {vendor.sourcing_rejection_reason && (
                            <Box sx={{ mb: 0.5, mt: 2 }}>
                              <CustomTypography
                                variant="body2"
                                style={{
                                  color: theme.palette.error.main,
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  marginBottom: "0.5rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Rejection Reason
                              </CustomTypography>
                              <CustomTypography variant="body2">
                                {vendor.sourcing_rejection_reason}
                              </CustomTypography>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          <Chip
                            label="Pending"
                            color="warning"
                            size="medium"
                            sx={{
                              fontWeight: 700,
                              px: 1,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid2>
                </Grid2>

                {/* Rejection History */}
                {vendor.rejection_history &&
                  vendor.rejection_history.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Divider sx={{ my: 3 }} />
                      <CustomTypography
                        variant="heading6"
                        fontWeight={700}
                        style={{
                          marginBottom: "16px",
                          color: theme.palette.customColors.blackBorder,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Rejection History
                      </CustomTypography>
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          overflowY: "auto",
                        }}
                      >
                        {vendor.rejection_history.map((history, index) => (
                          <Box
                            key={history.id || index}
                            sx={{
                              p: 2.5,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              backgroundColor: theme.palette.background.paper,
                              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
                            }}
                          >
                            <Grid2 container spacing={2}>
                              {(history.finance_rejection === 1 ||
                                history.sourcing_rejection === 1) && (
                                <>
                                  {history.finance_rejection === 1 && (
                                    <Grid2 size={{ xs: 12 }}>
                                      <Box
                                        sx={{
                                          p: 2,
                                          backgroundColor:
                                            theme.palette.error.main + "10",
                                          borderRadius: 1.5,
                                          border: `1.5px solid ${theme.palette.error.main}30`,
                                        }}
                                      >
                                        <Chip
                                          label="Finance Rejection"
                                          color="error"
                                          size="small"
                                          sx={{ mb: 1.5, fontWeight: 700 }}
                                        />
                                        {history.finance_rejection_reason && (
                                          <CustomTypography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.75rem",
                                              mb: 0.5,
                                              fontWeight: 700,
                                              color: theme.palette.error.main,
                                              textTransform: "uppercase",
                                              letterSpacing: "0.5px",
                                            }}
                                          >
                                            Reason:
                                          </CustomTypography>
                                        )}
                                        {history.finance_rejection_reason && (
                                          <CustomTypography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.875rem",
                                              mb: 1,
                                              wordBreak: "break-word",
                                              lineHeight: 1.6,
                                            }}
                                          >
                                            {history.finance_rejection_reason}
                                          </CustomTypography>
                                        )}
                                        {history.finance_rejection_date && (
                                          <CustomTypography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.75rem",
                                              color:
                                                theme.palette.text.secondary,
                                              fontWeight: 600,
                                            }}
                                          >
                                            <strong>Date:</strong>{" "}
                                            {formatDate(
                                              history.finance_rejection_date,
                                            )}
                                          </CustomTypography>
                                        )}
                                      </Box>
                                    </Grid2>
                                  )}
                                  {history.sourcing_rejection === 1 && (
                                    <Grid2 size={{ xs: 12 }}>
                                      <Box
                                        sx={{
                                          p: 2,
                                          backgroundColor:
                                            theme.palette.error.main + "10",
                                          borderRadius: 1.5,
                                          border: `1.5px solid ${theme.palette.error.main}30`,
                                        }}
                                      >
                                        <Chip
                                          label="Sourcing Rejection"
                                          color="error"
                                          size="small"
                                          sx={{ mb: 1.5, fontWeight: 700 }}
                                        />
                                        {history.sourcing_rejection_reason && (
                                          <CustomTypography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.75rem",
                                              mb: 0.5,
                                              fontWeight: 700,
                                              color: theme.palette.error.main,
                                              textTransform: "uppercase",
                                              letterSpacing: "0.5px",
                                            }}
                                          >
                                            Reason:
                                          </CustomTypography>
                                        )}
                                        {history.sourcing_rejection_reason && (
                                          <CustomTypography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.875rem",
                                              mb: 1,
                                              wordBreak: "break-word",
                                              lineHeight: 1.6,
                                            }}
                                          >
                                            {history.sourcing_rejection_reason}
                                          </CustomTypography>
                                        )}
                                        {history.sourcing_rejection_date && (
                                          <CustomTypography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.75rem",
                                              color:
                                                theme.palette.text.secondary,
                                              fontWeight: 600,
                                            }}
                                          >
                                            <strong>Date:</strong>{" "}
                                            {formatDate(
                                              history.sourcing_rejection_date,
                                            )}
                                          </CustomTypography>
                                        )}
                                      </Box>
                                    </Grid2>
                                  )}
                                </>
                              )}
                            </Grid2>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Basic Information */}
              <Box>
                <CustomTypography
                  variant="heading6"
                  style={{
                    fontWeight: 600,
                    marginBottom: 10,
                    color: theme.palette.customColors.blackBorder,
                  }}
                >
                  Basic Information
                </CustomTypography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Vendor ID
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.id || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Name
                    </CustomTypography>
                    <CustomTypography variant="body1" sx={{ fontWeight: 500 }}>
                      {vendor.name || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Email
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.email || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      GST Number
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.gst_number || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      PAN Number
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.pan_number || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Payment Term
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.payment_term + " Days" || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Creator
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.creator_name || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Created Date
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {formatDate(vendor.created_date)}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Modified Date
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {formatDate(vendor.modified_date)}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Types
                    </CustomTypography>
                    <Box style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {vendor.types && vendor.types.length > 0 ? (
                        vendor.types.map((type, index) => (
                          <Chip
                            key={type.type_id || index}
                            label={type.type}
                            size="small"
                            style={{
                              backgroundColor:
                                theme.palette.primary.main + "15",
                              color: theme.palette.primary.main,
                            }}
                          />
                        ))
                      ) : (
                        <CustomTypography variant="body2">-</CustomTypography>
                      )}
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Supplier Type
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.supplier_type || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                      }}
                    >
                      MSME Certified
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.msme_certified ? "Yes" : "No"}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>

              <Divider />

              {vendor.poc_list && vendor.poc_list.length > 0 && (
                <Box>
                  <CustomTypography
                    variant="heading6"
                    style={{
                      fontWeight: 600,
                      marginBottom: 10,
                      color: theme.palette.customColors.blackBorder,
                    }}
                  >
                    Point of Contact (POC)
                  </CustomTypography>
                  <Grid2 container spacing={2}>
                    {getSortedPocList().map((poc, index) => {
                      const isPrimary = isPrimaryPoc(poc);
                      return (
                        <Grid2 size={{ xs: 12, sm: 6 }} key={poc.id || index}>
                          <Box
                            sx={{
                              p: 2,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 2,
                              backgroundColor: theme.palette.background.paper,
                              position: "relative",
                            }}
                          >
                            {isPrimary && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 16,
                                  right: 16,
                                }}
                              >
                                <Chip
                                  label="Primary POC"
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      theme.palette.primary.main + "15",
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                  }}
                                />
                              </Box>
                            )}
                            <Grid2 container spacing={2}>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    marginBottom: 6,
                                  }}
                                >
                                  First Name
                                </CustomTypography>
                                <CustomTypography
                                  variant="body1"
                                  style={{ fontWeight: 500 }}
                                >
                                  {poc.first_name || "-"}
                                </CustomTypography>
                              </Grid2>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    marginBottom: 6,
                                  }}
                                >
                                  Last Name
                                </CustomTypography>
                                <CustomTypography
                                  variant="body1"
                                  style={{ fontWeight: 500 }}
                                >
                                  {poc.last_name || "-"}
                                </CustomTypography>
                              </Grid2>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    marginBottom: 6,
                                  }}
                                >
                                  Email
                                </CustomTypography>
                                <CustomTypography
                                  variant="body1"
                                  style={{
                                    fontWeight: 500,
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {poc.email || "-"}
                                </CustomTypography>
                              </Grid2>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <CustomTypography
                                  variant="body2"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    marginBottom: 6,
                                  }}
                                >
                                  Phone
                                </CustomTypography>
                                <CustomTypography
                                  variant="body1"
                                  style={{
                                    fontWeight: 500,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {poc.phone || "-"}
                                </CustomTypography>
                              </Grid2>
                            </Grid2>
                          </Box>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </Box>
              )}

              {vendor.poc_list && vendor.poc_list.length > 0 && <Divider />}

              {/* Bank Details */}
              <Box>
                <CustomTypography
                  variant="heading6"
                  style={{
                    fontWeight: 600,
                    marginBottom: 10,
                    color: theme.palette.customColors.blackBorder,
                  }}
                >
                  Bank Details
                </CustomTypography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Account Holder Name
                    </CustomTypography>

                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.bank_details?.account_holder_name || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Bank Name
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.bank_details?.bank_name || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Branch Name
                    </CustomTypography>

                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.bank_details?.branch_name || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Account Number
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.bank_details?.account_number || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      IFSC Code
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.bank_details?.ifsc_code || "-"}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>

              <Divider />

              {/* Documents & Links */}
              <Box>
                <CustomTypography
                  variant="heading6"
                  style={{
                    fontWeight: 600,
                    marginBottom: 10,
                    color: theme.palette.customColors.blackBorder,
                  }}
                >
                  Documents & Links
                </CustomTypography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      PAN Card
                    </CustomTypography>
                    {vendor.pan_card_link ? (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownloadLink(
                            vendor.pan_card_link!,
                            `PAN_Card_${vendor.id}.pdf`,
                          )
                        }
                        sx={{
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <CustomTypography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        -
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      GST Certificate
                    </CustomTypography>
                    {vendor.gstin_certificate_link ? (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownloadLink(
                            vendor.gstin_certificate_link!,
                            `GST_Certificate_${vendor.id}.pdf`,
                          )
                        }
                        sx={{
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <CustomTypography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        -
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Bank Details
                    </CustomTypography>
                    {vendor.bank_details_link ? (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownloadLink(
                            vendor.bank_details_link!,
                            `Bank_Details_${vendor.id}.pdf`,
                          )
                        }
                        sx={{
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <CustomTypography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        -
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      MSME Certificate
                    </CustomTypography>
                    {vendor.msme_link && vendor.msme_link.trim() !== "" ? (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownloadLink(
                            vendor.msme_link!,
                            `MSME_${vendor.id}.pdf`,
                          )
                        }
                        sx={{
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <CustomTypography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        -
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      MSME Declaration
                    </CustomTypography>
                    {vendor.msme_declaration_link &&
                    vendor.msme_declaration_link.trim() !== "" ? (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownloadLink(
                            vendor.msme_declaration_link!,
                            `MSME_Declaration_${vendor.id}.pdf`,
                          )
                        }
                        sx={{
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <CustomTypography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        -
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Company Website
                    </CustomTypography>
                    {vendor.company_website_link &&
                    vendor.company_website_link.trim() !== "" ? (
                      <Box
                        component="a"
                        href={ensureUrlProtocol(vendor.company_website_link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: theme.palette.primary.main,
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Visit Website
                      </Box>
                    ) : (
                      <CustomTypography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        -
                      </CustomTypography>
                    )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Products Catalog
                    </CustomTypography>
                    {vendor.products_catalog_link &&
                    vendor.products_catalog_link.trim() !== "" ? (
                      <Box
                        component="a"
                        href={ensureUrlProtocol(vendor.products_catalog_link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: theme.palette.primary.main,
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        View Catalog
                      </Box>
                    ) : (
                      <CustomTypography
                        variant="body2"
                        style={{ fontStyle: "italic" }}
                      >
                        -
                      </CustomTypography>
                    )}
                  </Grid2>
                </Grid2>
              </Box>

              <Divider />

              {/* Address Information */}
              <Box>
                <CustomTypography
                  variant="heading6"
                  style={{
                    fontWeight: 600,
                    marginBottom: 10,
                    color: theme.palette.customColors.blackBorder,
                  }}
                >
                  Address Information
                </CustomTypography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Address Line 1
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.address?.address_line_1 || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Address Line 2
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.address?.address_line_2 || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      City
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.address?.city || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      State
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.address?.state || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Country
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.address?.country || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Postal Code
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.address?.postal_code || "-"}
                    </CustomTypography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 6,
                      }}
                    >
                      Landmark
                    </CustomTypography>
                    <CustomTypography
                      variant="body1"
                      style={{ fontWeight: 500 }}
                    >
                      {vendor.address?.landmark || "-"}
                    </CustomTypography>
                  </Grid2>
                </Grid2>
              </Box>

              {/* Approve/Reject Buttons - at the end of content */}
              {(canApproveFinance ||
                canApproveSourcing ||
                canRejectFinance ||
                canRejectSourcing) && (
                <Box sx={{ mt: 3 }}>
                  {/* Finance Section */}
                  {(canApproveFinance || canRejectFinance) && (
                    <Box sx={{ mb: 3 }}>
                      <CustomTypography
                        variant="heading6"
                        style={{
                          fontWeight: 600,
                          marginBottom: 12,
                          color: theme.palette.customColors.blackBorder,
                        }}
                      >
                        Finance
                      </CustomTypography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {canApproveFinance && (
                          <AppButton
                            onClick={() => openApproveDialog("finance")}
                            variant="primary"
                            label="Approve"
                            disabled={isLoading || !hasFinancePermission}
                            customStyles={{ minWidth: 140 }}
                          />
                        )}
                        {canRejectFinance && (
                          <AppButton
                            onClick={() => openRejectDialog("finance")}
                            variant="secondary"
                            label="Reject"
                            disabled={isLoading || !hasFinancePermission}
                            customStyles={{ minWidth: 140 }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Sourcing Section */}
                  {(canApproveSourcing || canRejectSourcing) && (
                    <Box>
                      <CustomTypography
                        variant="heading6"
                        style={{
                          fontWeight: 600,
                          marginBottom: 12,
                          color: theme.palette.customColors.blackBorder,
                        }}
                      >
                        Sourcing
                      </CustomTypography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {canApproveSourcing && (
                          <AppButton
                            onClick={() => openApproveDialog("sourcing")}
                            variant="primary"
                            label="Approve"
                            disabled={isLoading || !hasSourcingPermission}
                            customStyles={{ minWidth: 140 }}
                          />
                        )}
                        {canRejectSourcing && (
                          <AppButton
                            onClick={() => openRejectDialog("sourcing")}
                            variant="secondary"
                            label="Reject"
                            disabled={isLoading || !hasSourcingPermission}
                            customStyles={{ minWidth: 140 }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>

            {/* Close Button */}
            <DialogActions
              sx={{
                padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <AppButton
                onClick={onClose}
                variant="cancel"
                label="Close"
                customStyles={{ minWidth: 100 }}
              />
            </DialogActions>

            <ConfirmationPopup
              id={vendor?.id ?? 0}
              open={showApproveDialog}
              title={`Approve ${approveType === "finance" ? "Finance" : "Sourcing"} Request`}
              content={`Are you sure you want to approve this ${approveType === "finance" ? "finance" : "sourcing"} vendor request?`}
              onConfirm={() => handleApprove()}
              onCancel={() => {
                setShowApproveDialog(false);
                setApproveType(null);
              }}
              slotProps={{
                root: { sx: { zIndex: 2100 } },
              }}
            />

            <ConfirmationPopup
              id={vendor?.id ?? 0}
              open={showRejectDialog}
              title={`Reject ${rejectType === "finance" ? "Finance" : "Sourcing"} Approval`}
              content="Please provide a reason for rejection."
              type="form"
              inputRequired={true}
              inputLabel="Rejection Reason"
              inputType="text"
              onConfirm={(_id, input) => {
                if (input !== undefined) handleReject(input);
              }}
              onCancel={() => {
                setShowRejectDialog(false);
                setRejectType(null);
              }}
              slotProps={{
                root: { sx: { zIndex: 2100 } },
                paper: { sx: { maxWidth: 480, width: "100%" } },
              }}
            />
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default observer(VendorDetailsPopup);
